import { LightningElement, wire } from "lwc";
import { ApolloQueryResult } from "@apollo/client";
import { LabelTranslations, MultiLabelAdapter } from "tbme/localization";
import { QueryAdapter } from "tbme/queryAdapter";
import { GetAgentblazerStatus, GetAgentblazerStatusVariables } from "./gql/GetAgentblazerStatus";
import { PublicProfile_trailheadStats } from "./gql/PublicProfile";
import { LearnerStatusLevel } from "./gql/LearnerStatusLevel";
import { LEARNER_STATUS_QUERY } from "./query";
import { chassis } from "tbme/localState";
import { getSharedUrlValues } from "../localState/urls";

type cardStates = "default" | "loading" | "loadFailed";

export interface AgentblazerStatusLevel extends LearnerStatusLevel {
  displayText?: string;
}

export interface AgentblazerYear {
  edition: string;
  levels: AgentblazerStatusLevel[]
}

const COMPONENT_LABELS = [
  "agentblazerStatusTitle",
  "agentblazerEmptyHeader",
  "agentblazerEmptySubheader",
  "agentblazerStartLearningButton",
  "failedToUpdate"
]

export default class extends LightningElement {
  private isSelf: boolean = undefined!;
  private trailheadAppUrl: string = undefined!;
  private variables: GetAgentblazerStatusVariables = {
    hasSlug: false,
  };
  private cardState: cardStates = "loading";
  private agentblazerStatusYears: AgentblazerYear[] = [];

  connectedCallback() {
    const urls = getSharedUrlValues() as any;
    this.trailheadAppUrl = `${urls.trailhead}/agentblazer`;

    const chassisData = chassis();
    const username = chassisData?.profile?.username;
    this.isSelf = chassisData?.isSelf || false;

    this.variables = {
      ...this.variables,
      slug: username,
      hasSlug: !!username,
    };
  }

  @wire(QueryAdapter, {
    query: LEARNER_STATUS_QUERY,
    variables: "$variables",
  })
  handleResult(result: ApolloQueryResult<GetAgentblazerStatus>) {
    const { data, loading, error, errors } = result;

    if (loading) {
      this.cardState = "loading";
      return;
    }

    if (error || errors) {
      this.cardState = "loadFailed";
      return;
    }

    if (
      !data.profile ||
      data.profile.__typename !== "PublicProfile" ||
      !data.profile.trailheadStats
    ) {
      this.cardState = "loadFailed";
      return;
    }

    this.updateAgentblazerStatus(data.profile.trailheadStats);
  }

  @wire(MultiLabelAdapter, {
    labels: COMPONENT_LABELS,
  })
  private labels: LabelTranslations = {};

  updateAgentblazerStatus = ({
    learnerStatusLevels,
  }: PublicProfile_trailheadStats) => {
    this.cardState = "default";

    if (!learnerStatusLevels || learnerStatusLevels.length === 0) {
      this.agentblazerStatusYears = [];
      return;
    }

    // Filter for Agentblazer status only
    const agentblazerLevels = learnerStatusLevels.filter(
      (record) => record?.statusName === "Agentblazer"
    ).map((record) => ({...record, displayText: `${record.title || ""} ${record.edition || ""}`.trim()})) as AgentblazerStatusLevel[];



    // Group levels by edition with progress == 100.0
    const editionLevelsMap = new Map<string, AgentblazerStatusLevel[]>();
    agentblazerLevels.forEach((record) => {
      if (record.progress === 100.0) {
        const edition = record.edition || "";
        const existing = editionLevelsMap.get(edition);

        if (!existing) {
          editionLevelsMap.set(edition, [record]);
        } else {
          editionLevelsMap.set(edition, [...existing, record])
        }
      }
    });

    let years: AgentblazerYear[] = Array.from(
      editionLevelsMap.entries()
    ).map(([edition, levels]) => ({ edition, levels }));

    years.sort((a, b) => {
      const yearA = Number(a.edition) || 0;
      const yearB = Number(b.edition) || 0;

      return yearB - yearA
    })

    this.agentblazerStatusYears = years;
  };

  private get hasActivity() {
    return (
      !this.isLoading &&
      !this.hasError &&
      this.agentblazerStatusYears.length > 0
    );
  }
  
  private get hasError() {
    return this.cardState === "loadFailed";
  }

  get isVisible() {
    if (this.isSelf) {
      return this.hasError || !this.isLoading
    }
    return this.hasActivity;
  }

  private get isLoading() {
    return this.cardState === "loading";
  }
}
